import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/shared/lib/utils';


const buttonVariants = cva(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
    {
        variants       : {
            variant: {
                default    : 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
                destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
                outline    : 'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
                secondary  : 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
                warning    : 'text-destructive-foreground border-orange-500/50 bg-orange-500/80',
                safe       : 'text-emerald-800 border-green-500/70 bg-green-500/30',
                ghost      : 'hover:bg-accent hover:text-accent-foreground',
                link       : 'text-primary underline-offset-4 hover:underline',
            },
            size   : {
                default: 'h-9 px-4 py-2',
                sm     : 'h-8 rounded-md px-3 text-xs',
                lg     : 'h-10 rounded-md px-8',
                icon   : 'h-9 w-9',
            },
        },
        defaultVariants: {
            variant: 'default',
            size   : 'default',
        },
    },
);


export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}


const Button       = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        return (
            <Comp
                className={ cn(buttonVariants({ variant, size, className })) }
                ref={ ref }
                { ...props }
            />
        );
    },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
